import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import { RefreshCw, MailCheck, MailX, Clock, Users, FlaskConical } from "lucide-react";

const EmailModeBadge = ({ mode }) => {
  if (!mode) return null;
  const live = mode.mode === "LIVE";
  return (
    <div className={`rounded-xl border-2 px-4 py-3 flex items-center gap-3 ${live ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`} data-testid="email-mode-banner">
      <span className={`h-3 w-3 rounded-full ${live ? "bg-emerald-500" : "bg-amber-500"} animate-pulse`} />
      <div>
        <p className={`font-heading font-bold text-sm ${live ? "text-emerald-900" : "text-amber-900"}`} data-testid="email-mode-label">
          EMAIL MODE: {mode.mode}
        </p>
        <p className={`text-xs ${live ? "text-emerald-800" : "text-amber-800"}`}>{mode.note}</p>
      </div>
    </div>
  );
};

const STATUS_LABELS = {
  unverified: { label: "Unverified", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  sent: { label: "Verification Sent", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  verified: { label: "Verified", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  expired: { label: "Expired", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  failed: { label: "Failed", cls: "bg-rose-50 text-rose-800 border-rose-300" },
};

const Tile = ({ label, value, testId, icon: Icon, accent }) => (
  <Card><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground">
      {Icon && <Icon className={`h-4 w-4 ${accent || "text-primary"}`} />}
      <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
    </div>
    <p className="font-heading font-bold text-2xl tabular-nums mt-1.5" data-testid={testId}>{value}</p>
  </CardContent></Card>
);

export default function AdminIdentity() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    adminApi.get("/admin/identity/summary").then((r) => setSummary(r.data)).catch(() => toast.error("Could not load identity summary"));
  }, []);
  const loadRows = useCallback(() => {
    const params = {};
    if (filter !== "all") params.status = filter;
    adminApi.get("/admin/identity/participants", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRows(); }, [loadRows]);

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-identity-title">Identity & Verification</h1>
          <p className="text-sm text-muted-foreground mt-1">Email verification status, delivery health and resend volume. Tokens are never shown in plaintext.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { load(); loadRows(); }} data-testid="identity-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
      </div>

      {summary && <EmailModeBadge mode={summary.email_mode} />}

      {!summary ? (
        <div className="grid sm:grid-cols-4 gap-3 mt-5"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Tile label="Participants" value={summary.participants_total} testId="id-tile-total" icon={Users} />
          <Tile label="Verified" value={summary.verified} testId="id-tile-verified" icon={MailCheck} accent="text-emerald-600" />
          <Tile label="Unverified" value={summary.unverified} testId="id-tile-unverified" icon={Clock} accent="text-amber-600" />
          <Tile label="Emails sent" value={summary.verification_emails_sent} testId="id-tile-sent" icon={MailCheck} />
          <Tile label="Delivery failures" value={summary.verification_email_failures} testId="id-tile-failures" icon={MailX} accent="text-rose-600" />
          <Tile label="Expired tokens" value={summary.expired_tokens} testId="id-tile-expired" icon={Clock} accent="text-amber-600" />
          <Tile label="Resend volume" value={summary.resend_volume} testId="id-tile-resends" icon={RefreshCw} />
          <Tile label="Acceptance test" value={summary.acceptance_test_participants} testId="id-tile-test" icon={FlaskConical} accent="text-amber-600" />
        </div>
      )}

      <div className="flex items-center gap-3 mt-7 mb-3">
        <p className="text-xs text-muted-foreground">Filter</p>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52 bg-white" data-testid="identity-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All participants</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Participant</TableHead><TableHead>Email (masked)</TableHead>
              <TableHead>Status</TableHead><TableHead>Sent</TableHead><TableHead>Sends</TableHead><TableHead>Verified at</TableHead>
            </TableRow></TableHeader>
            <TableBody data-testid="identity-participants-table">
              {rows.map((p) => {
                const st = p.email_verified ? STATUS_LABELS.verified : (STATUS_LABELS[p.verification_status] || STATUS_LABELS.unverified);
                return (
                  <TableRow key={p.id}>
                    <TableCell className="text-sm">{p.name || "—"}{p.is_test && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-300 text-[9px]">TEST</Badge>}</TableCell>
                    <TableCell className="font-mono text-xs">{p.email_masked}</TableCell>
                    <TableCell><Badge variant="outline" className={`${st.cls} text-[10px]`}>{st.label}</Badge></TableCell>
                    <TableCell className="text-xs">{p.verification_sent_at ? new Date(p.verification_sent_at).toLocaleString("en-CA") : "—"}</TableCell>
                    <TableCell className="tabular-nums text-sm">{p.verification_send_count || 0}</TableCell>
                    <TableCell className="text-xs">{p.verified_at ? new Date(p.verified_at).toLocaleString("en-CA") : "—"}</TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No participants match this filter.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
